import { useEffect, useCallback, useRef } from 'react';
import { useEvent, useStoreMap, useStore } from 'effector-react';
import stringToPath from 'lodash.topath';
import { createEvent, sample, createStore, combine, forward, guard, is } from 'effector';

function _extends() {
  _extends = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends.apply(this, arguments);
}

/**
 * @param {object} state Redux state
 * @param path
 * @param removeEmpty
 * @param inDeep should be false for inlineMap
 * @param index
 * @returns {object} State
 */

var deleteIn = function deleteIn(state, path, removeEmpty, inDeep, // false for inlineMap
index) {
  if (removeEmpty === void 0) {
    removeEmpty = false;
  }

  if (inDeep === void 0) {
    inDeep = true;
  }

  if (index === void 0) {
    index = 0;
  }

  var pathArray = index === 0 ? stringToPath(path) : path;

  if (!inDeep) {
    pathArray = [path];
  }

  var currentKey = pathArray[index];

  if (!state) {
    return state;
  }

  var isEndPoint = pathArray.length - 1 === index;
  var isArray = Array.isArray(state);
  var isObject = !isArray && typeof state === 'object'; // @ts-ignore

  var newState = isArray ? [].concat(state) : _extends({}, state);

  if (isObject) {
    if (isEndPoint) {
      // tslint:disable-next-line:no-dynamic-delete
      delete newState[currentKey];
      return newState;
    } else {
      var result = deleteIn(newState[currentKey], pathArray, removeEmpty, inDeep, index + 1);
      var isRemoveEmpty = !result || removeEmpty && (!result || !Object.keys(result).length);

      if (isRemoveEmpty) {
        // tslint:disable-next-line:no-dynamic-delete
        delete newState[currentKey];
      } else {
        newState[currentKey] = result;
      }

      return newState;
    }
  } else if (isArray) {
    if (isEndPoint) {
      // @ts-ignore
      return newState.filter(function (_, i) {
        return Number(currentKey) !== i;
      });
    } else {
      var _result = deleteIn(newState[currentKey], pathArray, removeEmpty, inDeep, index + 1);

      var _isRemoveEmpty = !_result || removeEmpty && (!_result || !Object.keys(_result).length);

      if (_isRemoveEmpty) {
        // @ts-ignore
        return newState.filter(function (_, i) {
          return Number(currentKey) !== i;
        });
      } else {
        newState[currentKey] = _result;
      }

      return newState;
    }
  }
};
/**
 *
 * @param {object} state Object will be expand
 * @param {string} path Path in object
 * @param {*} value Value will be add
 * @param {number} pathIndex Not require
 * @returns {object} State
 */

var setIn = function setIn(state, path, value, pathIndex) {
  var _extends2;

  if (pathIndex === void 0) {
    pathIndex = 0;
  }

  var pathArray = pathIndex === 0 ? stringToPath(path) : path;

  if (pathIndex >= pathArray.length) {
    return value;
  }

  var first = pathArray[pathIndex];
  var firstState = state && (Array.isArray(state) ? state[Number(first)] : state[first]);
  var next = setIn(firstState, pathArray, value, pathIndex + 1);

  if (!state) {
    if (isNaN(first)) {
      var _ref;

      return _ref = {}, _ref[first] = next, _ref;
    }

    var initialized = []; // @ts-ignore

    initialized[parseInt(first, 10)] = next; // @ts-ignore

    return initialized;
  }

  if (Array.isArray(state)) {
    // @ts-ignore
    var copy = [].concat(state); // @ts-ignore

    copy[parseInt(first, 10)] = next; // @ts-ignore

    return copy;
  } // @ts-ignore


  return _extends({}, state, (_extends2 = {}, _extends2[first] = next, _extends2));
};
/**
 *
 * @param {object} state Redux state
 * @param {string} field Path to field
 * @param {*?} defaultValue
 * @returns {object} State
 */

var getIn = function getIn(state, field, defaultValue) {
  var _result2;

  if (!state) {
    return defaultValue;
  }

  var path = Array.isArray(field) ? field : stringToPath(field);
  var length = path.length;

  if (!length) {
    return defaultValue;
  }

  var result = state;

  for (var i = 0; i < length && result; i += 1) {
    result = result[path[i]];
  } // @ts-ignore


  return (_result2 = result) != null ? _result2 : defaultValue;
};
var makeNested = function makeNested(inlineMap) {
  return Object.entries(inlineMap).reduce(function (acc, _ref2) {
    var key = _ref2[0],
        value = _ref2[1];
    return setIn(acc, key, value);
  }, {});
};

var isFieldMeta = function isFieldMeta(value) {
  return typeof value === 'object' && value._type === 'fieldMeta';
};

var removeFromInlineMap = function removeFromInlineMap(map, key) {
  var nestedMap = deleteIn(makeNested(map), key);
  var nodes = [{
    node: nestedMap,
    path: []
  }];
  var newInlineMap = {};

  var _loop = function _loop() {
    var currentNode = nodes.pop();
    Object.entries(currentNode.node).forEach(function (_ref3) {
      var k = _ref3[0],
          v = _ref3[1];

      if (isFieldMeta(v)) {
        newInlineMap[currentNode.path.concat(k).join('.')] = v;
      } else {
        nodes.push({
          node: v,
          path: currentNode.path.concat(k)
        });
      }
    });
  };

  while (nodes.length) {
    _loop();
  }

  return newInlineMap;
};
var makeConsistentKey = function makeConsistentKey(key) {
  if (Array.isArray(key)) {
    return key.join('.');
  }

  return stringToPath(key).join('.');
};

function getInTs(obj) {
  for (var _len = arguments.length, keys = new Array(_len > 1 ? _len - 1 : 0), _key = 1; _key < _len; _key++) {
    keys[_key - 1] = arguments[_key];
  }

  return getIn(obj, keys);
}

var getName = function getName() {
  for (var _len = arguments.length, keys = new Array(_len), _key = 0; _key < _len; _key++) {
    keys[_key] = arguments[_key];
  }

  return keys;
};

var getNameStr = function getNameStr() {
  for (var _len = arguments.length, keys = new Array(_len), _key = 0; _key < _len; _key++) {
    keys[_key] = arguments[_key];
  }

  return keys.join('.');
};

var initialFieldState = {
  _type: 'fieldMeta',
  active: false,
  touched: false,
  changed: false,
  blurred: false,
  touchedAfterOuterError: false,
  changedAfterOuterError: false,
  blurredAfterOuterError: false
};
var initialFormState = {
  submitted: false,
  hasError: false,
  hasOuterError: false
};

var useForm = function useForm(_ref) {
  var form = _ref.form,
      meta = _ref.meta,
      _ref$resetUnmount = _ref.resetUnmount,
      resetUnmount = _ref$resetUnmount === void 0 ? true : _ref$resetUnmount;
  var $values = form.$values,
      $form = form.$form,
      $fieldsInline = form.$fieldsInline,
      $errorsInline = form.$errorsInline,
      $outerErrorsInline = form.$outerErrorsInline;
  var setMeta = useEvent(form.setMeta);
  var setValue = useEvent(form.setValue);
  var setValues = useEvent(form.setValues);
  var setOrDeleteError = useEvent(form.setOrDeleteError);
  var setFieldState = useEvent(form.setFieldState);
  var setOrDeleteOuterError = useEvent(form.setOrDeleteOuterError);
  var setOuterErrorsInlineState = useEvent(form.setOuterErrorsInlineState);
  var validateForm = useEvent(form.validateForm);
  var submit = useEvent(form.submit);
  var reset = useEvent(form.reset);
  var onChangeFieldBrowser = useEvent(form.onChangeFieldBrowser);
  var onFocusFieldBrowser = useEvent(form.onFocusFieldBrowser);
  var onBlurFieldBrowser = useEvent(form.onBlurFieldBrowser);
  var fieldInit = useEvent(form.fieldInit);
  useEffect(function () {
    validateForm();
    return function () {
      if (resetUnmount) {
        reset();
      }
    };
  }, []);
  useEffect(function () {
    setMeta(meta);
  }, [meta]);
  var controller = useCallback(function (_ref2) {
    var nameProp = _ref2.name,
        validate = _ref2.validate,
        flat = _ref2.flat;
    return function () {
      var refName = useRef(makeConsistentKey(nameProp));
      refName.current = makeConsistentKey(nameProp);
      var refFlat = useRef(flat);
      refFlat.current = flat;
      useEffect(function () {
        fieldInit({
          name: refName.current,
          validate: validate,
          flat: flat
        });
      }, []);
      var value = useStoreMap({
        store: $values,
        keys: [refName.current],
        fn: function fn(values, _ref3) {
          var _ref4;

          var field = _ref3[0];
          return (_ref4 = flat ? values[field] : getIn(values, field)) != null ? _ref4 : null;
        }
      });
      var innerError = useStoreMap({
        store: $errorsInline,
        keys: [refName.current],
        fn: function fn(errorsInline, _ref5) {
          var field = _ref5[0];
          return errorsInline[field] || null;
        }
      });
      var outerError = useStoreMap({
        store: $outerErrorsInline,
        keys: [refName.current],
        fn: function fn(outerErrorsInline, _ref6) {
          var field = _ref6[0];
          return outerErrorsInline[field] || null;
        }
      });
      var error = outerError || innerError;
      var fieldState = useStoreMap({
        store: $fieldsInline,
        keys: [refName.current],
        fn: function fn(fieldsInline, _ref7) {
          var field = _ref7[0];
          return fieldsInline[field] || initialFieldState;
        }
      });
      var formSubmitted = useStoreMap({
        store: $form,
        keys: [],
        fn: function fn(formState) {
          return formState.submitted;
        }
      });
      var formHasError = useStoreMap({
        store: $form,
        keys: [],
        fn: function fn(formState) {
          return formState.hasError;
        }
      });
      var formHasOuterError = useStoreMap({
        store: $form,
        keys: [],
        fn: function fn(formState) {
          return formState.hasOuterError;
        }
      });
      var formState = {
        submitted: formSubmitted,
        hasError: formHasError,
        hasOuterError: formHasOuterError
      };
      var isShowInnerError = (formState.submitted || fieldState.blurred) && Boolean(innerError);
      var isShowOuterError = !fieldState.changedAfterOuterError && Boolean(outerError);
      var isShowError = isShowInnerError || isShowOuterError;
      return {
        input: {
          name: refName.current,
          value: value,
          onChange: function onChange(eventOrValue) {
            return onChangeFieldBrowser({
              event: eventOrValue,
              name: refName.current,
              flat: refFlat.current
            });
          },
          onFocus: function onFocus(event) {
            return onFocusFieldBrowser({
              event: event,
              name: refName.current
            });
          },
          onBlur: function onBlur(event) {
            return onBlurFieldBrowser({
              event: event,
              name: refName.current
            });
          }
        },
        form: formState,
        meta: meta,
        fieldState: fieldState,
        error: error,
        innerError: innerError,
        outerError: outerError,
        isShowError: isShowError,
        isShowOuterError: isShowOuterError,
        isShowInnerError: isShowInnerError,
        validate: validate,
        setFieldState: setFieldState,
        setOrDeleteError: setOrDeleteError,
        setOrDeleteOuterError: setOrDeleteOuterError,
        setOuterErrorsInlineState: setOuterErrorsInlineState
      };
    };
  }, []);
  var handleSubmit = useCallback(function (e) {
    e.preventDefault();
    submit();
  }, []);
  return {
    controller: controller,
    handleSubmit: handleSubmit,
    setMeta: setMeta,
    setValue: setValue,
    setValues: setValues,
    setOrDeleteError: setOrDeleteError,
    setFieldState: setFieldState,
    setOrDeleteOuterError: setOrDeleteOuterError,
    setOuterErrorsInlineState: setOuterErrorsInlineState,
    validateForm: validateForm,
    submit: submit,
    reset: reset,
    fieldInit: fieldInit
  };
};

var useFieldArray = function useFieldArray(_ref) {
  var fieldArray = _ref.fieldArray,
      name = _ref.name;
  var refName = useRef(name);
  refName.current = name;
  var $values = fieldArray.form.$values;

  var _push = useEvent(fieldArray.push);

  var _remove = useEvent(fieldArray.remove);

  var values = useStore($values);
  var map = useCallback(function (callback) {
    var results = [];
    var fields = getIn(values, refName.current, []);
    fields.forEach(function (field, index) {
      var callbackResult = callback({
        formItemName: refName.current + "." + index,
        fields: fields,
        field: field,
        index: index
      });
      results.push(callbackResult);
    });
    return results;
  }, [values]); // todo Fix type

  return {
    map: map,
    remove: function remove(index) {
      return _remove({
        fieldName: refName.current,
        index: index
      });
    },
    push: function push(value) {
      return _push({
        fieldName: refName.current,
        value: value
      });
    }
  };
};

var useError = function useError(_ref) {
  var name = _ref.name,
      form = _ref.form;
  var $values = form.$values,
      $fieldsInline = form.$fieldsInline,
      $errorsInline = form.$errorsInline,
      $outerErrorsInline = form.$outerErrorsInline,
      $form = form.$form;
  var value = useStoreMap({
    store: $values,
    keys: [name],
    fn: function fn(values, _ref2) {
      var field = _ref2[0];
      return getIn(values, field) || null;
    }
  });
  var innerError = useStoreMap({
    store: $errorsInline,
    keys: [name],
    fn: function fn(errorsInline, _ref3) {
      var field = _ref3[0];
      return errorsInline[field] || null;
    }
  });
  var outerError = useStoreMap({
    store: $outerErrorsInline,
    keys: [name],
    fn: function fn(outerErrorsInline, _ref4) {
      var field = _ref4[0];
      return outerErrorsInline[field] || null;
    }
  });
  var error = innerError || outerError;
  var fieldState = useStoreMap({
    store: $fieldsInline,
    keys: [name],
    fn: function fn(fieldsInline, _ref5) {
      var field = _ref5[0];
      return fieldsInline[field] || initialFieldState;
    }
  });
  var formSubmitted = useStoreMap({
    store: $form,
    keys: [],
    fn: function fn(formState) {
      return formState.submitted;
    }
  });
  var formHasError = useStoreMap({
    store: $form,
    keys: [],
    fn: function fn(formState) {
      return formState.hasError;
    }
  });
  var formHasOuterError = useStoreMap({
    store: $form,
    keys: [],
    fn: function fn(formState) {
      return formState.hasOuterError;
    }
  });
  var formState = {
    submitted: formSubmitted,
    hasError: formHasError,
    hasOuterError: formHasOuterError
  };
  var meta = useStore(form.$meta);
  var isShowInnerError = (formState.submitted || fieldState.blurred) && Boolean(innerError);
  var isShowOuterError = !fieldState.changedAfterOuterError && Boolean(outerError);
  var isShowError = isShowInnerError || isShowOuterError;
  return {
    inputValue: value,
    form: formState,
    meta: meta,
    fieldState: fieldState,
    error: error,
    innerError: innerError,
    outerError: outerError,
    isShowError: isShowError,
    isShowOuterError: isShowOuterError,
    isShowInnerError: isShowInnerError
  };
};

var createFieldArray = function createFieldArray(_ref) {
  var form = _ref.form,
      domain = _ref.domain;
  var $values = form.$values,
      $fieldsInline = form.$fieldsInline;
  var createEvent$1 = domain ? domain.createEvent : createEvent;
  var remove = createEvent$1("hookForm_" + form.name + "_fieldArray_Remove");
  var push = createEvent$1("hookForm_" + form.name + "_fieldArray_Push"); // const unshift = useMemo(() => createEvent<any>('hookForm_fieldArray_Unshift'), []);
  // const move = useMemo(() => createEvent<{ from: number; to: number }>('hookForm_fieldArray_Move'), []);
  // const insert = useMemo(() => createEvent<{ value: any; index: number }>('hookForm_fieldArray_Insert'), []);
  // const swap = useMemo(() => createEvent<{ from: number; to: number }>('hookForm_fieldArray_Swap'), []);

  $fieldsInline.on(remove, function (fieldsInline, _ref2) {
    var index = _ref2.index,
        fieldName = _ref2.fieldName;
    return removeFromInlineMap(fieldsInline, fieldName + "." + index);
  });
  $values.on(remove, function (values, _ref3) {
    var fieldName = _ref3.fieldName,
        index = _ref3.index;
    var newFields = getIn(values, fieldName, []).filter(function (_, i) {
      return i !== index;
    });
    return setIn(values, fieldName, newFields);
  }).on(push, function (values, _ref4) {
    var fieldName = _ref4.fieldName,
        value = _ref4.value;
    var newFields = [].concat(getIn(values, fieldName, []));

    if (Array.isArray(value)) {
      newFields.push.apply(newFields, value);
    } else {
      newFields.push(value);
    }

    return setIn(values, fieldName, newFields);
  }); // $values.on(unshift, (values, value) => {
  //   // todo implement for fieldsInline
  //   const newFields = [value, ...getIn(values, refName.current, [])];
  //   return setIn(values, refName.current, newFields);
  // });
  // $values.on(move, (values, { from, to }) => {
  //   // todo implement for fieldsInline
  //   const fields = getIn(values, refName.current, []);
  //   const newFields = [];
  //   let movingField = {};
  //   fields.forEach((field, i) => {
  //     if (from === i) {
  //       movingField = field;
  //     } else if (to === i) {
  //       newFields.push(field);
  //       newFields.push(movingField);
  //     } else {
  //       newFields.push(field);
  //     }
  //   });
  //   return setIn(values, refName.current, newFields);
  // });
  // $values.on(insert, (values, { index, value }) => {
  //   // todo implement for fieldsInline
  //   const fields = getIn(values, refName.current);
  //   const newFields = [];
  //   fields.forEach((field, i) => {
  //     if (index === i) {
  //       newFields.push(value);
  //       newFields.push(field);
  //     } else {
  //       newFields.push(field);
  //     }
  //   });
  //   return setIn(values, refName.current, newFields);
  // });
  //
  // $values.on(swap, (values, { from, to }) => {
  //   // todo implement for fieldsInline
  //   const fields = getIn(values, refName.current);
  //   const newFields = [];
  //   fields.forEach((field, i) => {
  //     if (from === i) {
  //       newFields.push(fields[to]);
  //     } else if (to === i) {
  //       newFields.push(fields[from]);
  //     } else {
  //       newFields.push(field);
  //     }
  //   });
  //   return setIn(values, refName.current, newFields);
  // });

  sample({
    source: form.$allFormState,
    clock: form.fieldInit,
    target: form.validateForm
  });
  return {
    form: form,
    remove: remove,
    push: push
  };
};

function _objectWithoutPropertiesLoose(source, excluded) {
  if (source == null) return {};
  var target = {};
  var sourceKeys = Object.keys(source);
  var key, i;

  for (i = 0; i < sourceKeys.length; i++) {
    key = sourceKeys[i];
    if (excluded.indexOf(key) >= 0) continue;
    target[key] = source[key];
  }

  return target;
}

function _createForOfIteratorHelperLoose(o, allowArrayLike) { var it; if (typeof Symbol === "undefined" || o[Symbol.iterator] == null) { if (Array.isArray(o) || (it = _unsupportedIterableToArray(o)) || allowArrayLike && o && typeof o.length === "number") { if (it) o = it; var i = 0; return function () { if (i >= o.length) return { done: true }; return { done: false, value: o[i++] }; }; } throw new TypeError("Invalid attempt to iterate non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method."); } it = o[Symbol.iterator](); return it.next.bind(it); }

function _unsupportedIterableToArray(o, minLen) { if (!o) return; if (typeof o === "string") return _arrayLikeToArray(o, minLen); var n = Object.prototype.toString.call(o).slice(8, -1); if (n === "Object" && o.constructor) n = o.constructor.name; if (n === "Map" || n === "Set") return Array.from(o); if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray(o, minLen); }

function _arrayLikeToArray(arr, len) { if (len == null || len > arr.length) len = arr.length; for (var i = 0, arr2 = new Array(len); i < len; i++) { arr2[i] = arr[i]; } return arr2; }

var isEvent = function isEvent(candidate) {
  return !!(candidate && candidate.stopPropagation && candidate.preventDefault);
};

var getSelectedValues = function getSelectedValues(options) {
  var result = [];

  if (options) {
    for (var _iterator = _createForOfIteratorHelperLoose(options), _step; !(_step = _iterator()).done;) {
      var option = _step.value;

      if (option.selected) {
        // @ts-ignore
        result.push(option.value);
      }
    }
  }

  return result;
};

var getValue = function getValue(event, isReactNative) {
  if (isEvent(event)) {
    if (!isReactNative && event.nativeEvent && event.nativeEvent.text !== undefined) {
      return event.nativeEvent.text;
    }

    if (isReactNative && event.nativeEvent !== undefined) {
      return event.nativeEvent.text;
    }

    var detypedEvent = event;
    var _detypedEvent$target = detypedEvent.target,
        type = _detypedEvent$target.type,
        value = _detypedEvent$target.value,
        checked = _detypedEvent$target.checked,
        files = _detypedEvent$target.files,
        dataTransfer = detypedEvent.dataTransfer;

    if (type === 'checkbox') {
      return !!checked;
    }

    if (type === 'file') {
      return files || dataTransfer && dataTransfer.files;
    }

    if (type === 'select-multiple') {
      return getSelectedValues(event.target.options);
    }

    return value;
  }

  return event;
};

var createForm = function createForm(_temp) {
  var _ref = _temp === void 0 ? {} : _temp,
      name = _ref.name,
      validate = _ref.validate,
      _ref$mapSubmit = _ref.mapSubmit,
      mapSubmit = _ref$mapSubmit === void 0 ? function (params) {
    return params;
  } : _ref$mapSubmit,
      onSubmitArg = _ref.onSubmit,
      _ref$onSubmitGuardFn = _ref.onSubmitGuardFn,
      onSubmitGuardFn = _ref$onSubmitGuardFn === void 0 ? function (_ref2) {
    var form = _ref2.form;
    return !form.hasError;
  } : _ref$onSubmitGuardFn,
      onChangeArg = _ref.onChange,
      _ref$onChangeGuardFn = _ref.onChangeGuardFn,
      onChangeGuardFn = _ref$onChangeGuardFn === void 0 ? function (_ref3) {
    var form = _ref3.form;
    return !form.hasError;
  } : _ref$onChangeGuardFn,
      initialValues = _ref.initialValues,
      _ref$initialMeta = _ref.initialMeta,
      initialMeta = _ref$initialMeta === void 0 ? {} : _ref$initialMeta,
      domain = _ref.domain,
      _ref$resetOuterErrors = _ref.resetOuterErrorsBySubmit,
      resetOuterErrorsBySubmit = _ref$resetOuterErrors === void 0 ? true : _ref$resetOuterErrors,
      _ref$resetOuterErrorB = _ref.resetOuterErrorByOnChange,
      resetOuterErrorByOnChange = _ref$resetOuterErrorB === void 0 ? true : _ref$resetOuterErrorB;

  var createEvent$1 = domain ? domain.createEvent : createEvent;
  var createStore$1 = domain ? domain.createStore : createStore;
  var setMeta = createEvent$1("Form_" + name + "_SetMeta");
  var setValue = createEvent$1("Form_" + name + "_SetValue");
  var setValues = createEvent$1("Form_" + name + "_SetValues");
  var setOrDeleteError = createEvent$1("Form_" + name + "_SetOrDeleteError");
  var setErrorsInlineState = createEvent$1("Form_" + name + "_SetErrorsInlineState");
  var setFieldState = createEvent$1("Form_" + name + "_SetFieldState");
  var setSubmitted = createEvent$1("Form_" + name + "_SetSubmitted");
  var resetOuterFieldStateFlags = createEvent$1("Form_" + name + "_ResetOuterFieldStateFlags");
  var resetOuterErrors = createEvent$1("Form_" + name + "_ResetOuterErrors");
  var resetOuterError = createEvent$1("Form_" + name + "_ResetOuterError");
  var setOrDeleteOuterError = createEvent$1("Form_" + name + "_SetOrDeleteOuterError");
  var reset = createEvent$1("Form_" + name + "_Reset");
  var setOuterErrorsInlineState = createEvent$1("Form_" + name + "_SetOuterErrorsInlineState");
  var validateForm = createEvent$1("Form_" + name + "_ValidateForm");
  var submit = createEvent$1("Form_" + name + "_Submit");
  var onSubmit = createEvent$1("Form_" + name + "_OnSubmit");
  var onChange = createEvent$1("Form_" + name + "_OnChange");
  var $values = createStore$1(initialValues || {}, {
    name: "Form_" + name + "_$values"
  });
  var $errorsInline = createStore$1({}, {
    name: "Form_" + name + "_$errorsInline"
  });
  var $outerErrorsInline = createStore$1({}, {
    name: "Form_" + name + "_$outerErrorsInline"
  });
  var $fieldsInline = createStore$1({}, {
    name: "Form_" + name + "_$fieldsInline"
  });
  var $fieldsInlineInitData = createStore$1({}, {
    name: "Form_" + name + "_$fieldsInlineInitData"
  });
  var $form = createStore$1(initialFormState, {
    name: "Form_" + name + "_$form"
  });
  var $meta = createStore$1(initialMeta, {
    name: "Form_" + name + "_$meta"
  });
  var $allFormState = combine({
    values: $values,
    errorsInline: $errorsInline,
    outerErrorsInline: $outerErrorsInline,
    fieldsInline: $fieldsInline,
    form: $form,
    meta: $meta
  });
  var onChangeFieldBrowser = createEvent$1("Form_" + name + "_OnChange");
  var onChangeField = onChangeFieldBrowser.map(function (_ref4) {
    var name = _ref4.name,
        event = _ref4.event,
        flat = _ref4.flat;
    return {
      value: getValue(event),
      name: name,
      flat: flat
    };
  });
  var onFocusFieldBrowser = createEvent$1("Form_" + name + "_OnFocus");
  var onBlurFieldBrowser = createEvent$1("Form_" + name + "_OnBlur");
  var fieldInit = createEvent$1("Form_" + name + "_fieldInit");

  var validateByValues = function validateByValues(_ref5) {
    var values = _ref5.values,
        fieldsInline = _ref5.fieldsInline,
        rest = _objectWithoutPropertiesLoose(_ref5, ["values", "fieldsInline"]);

    var errorsInlineState = {};
    Object.entries(fieldsInline).forEach(function (_ref6) {
      var name = _ref6[0],
          validate = _ref6[1].validate;
      var error = validate && validate(getIn(values, name));

      if (error) {
        errorsInlineState[name] = validate && validate(getIn(values, name));
      }
    });

    if (validate) {
      var formLevelErrorsInlineState = validate(_extends({}, rest, {
        values: values,
        errorsInline: errorsInlineState,
        fieldsInline: fieldsInline
      }));
      Object.entries(formLevelErrorsInlineState).forEach(function (_ref7) {
        var name = _ref7[0],
            error = _ref7[1];

        if (error) {
          errorsInlineState[name] = error;
        } else {
          delete errorsInlineState[name];
        }
      });
    }

    return errorsInlineState;
  };

  if (resetOuterErrorByOnChange) {
    sample({
      source: onChangeField,
      fn: function fn(_ref8) {
        var name = _ref8.name;
        return name;
      },
      target: resetOuterError
    });
  }

  forward({
    from: submit,
    to: [validateForm, resetOuterFieldStateFlags]
  });

  if (resetOuterErrorsBySubmit) {
    forward({
      from: submit,
      to: resetOuterErrors
    });
  }

  sample({
    source: resetOuterErrors,
    fn: function fn() {
      return {};
    },
    target: $outerErrorsInline
  });
  sample({
    source: $allFormState,
    clock: validateForm,
    fn: function fn(params) {
      return validateByValues(params);
    },
    target: $errorsInline
  });
  sample({
    source: $allFormState,
    clock: $values,
    fn: function fn(params) {
      return validateByValues(params);
    },
    target: $errorsInline
  });
  sample({
    source: $allFormState,
    clock: guard({
      source: submit,
      filter: $allFormState.map(onSubmitGuardFn)
    }),
    fn: mapSubmit,
    target: onSubmit
  });
  sample({
    source: $allFormState,
    clock: guard({
      source: onChangeFieldBrowser,
      filter: $allFormState.map(onChangeGuardFn)
    }),
    fn: mapSubmit,
    target: onChange
  });

  if (onSubmitArg) {
    if (is.effect(onSubmitArg) || is.event(onSubmitArg)) {
      forward({
        from: onSubmit,
        to: onSubmitArg
      });
    } else if (typeof onSubmitArg === 'function') {
      onSubmit.watch(onSubmitArg);
    }
  }

  if (onChangeArg) {
    if (is.effect(onChangeArg) || is.event(onChangeArg)) {
      forward({
        from: onChange,
        to: onChangeArg
      });
    } else if (typeof onChangeArg === 'function') {
      onChange.watch(onChangeArg);
    }
  }

  $values.on(setValue, function (state, _ref9) {
    var field = _ref9.field,
        value = _ref9.value;
    return setIn(state, field, value);
  }).on(setValues, function (_, values) {
    return values;
  }).on(onChangeField, function (state, _ref10) {
    var _extends2;

    var value = _ref10.value,
        name = _ref10.name,
        flat = _ref10.flat;
    return flat ? _extends({}, state, (_extends2 = {}, _extends2[name] = value, _extends2)) : setIn(state, name, value);
  }).reset(reset);
  $errorsInline.on(setOrDeleteError, function (state, _ref11) {
    var _extends3;

    var field = _ref11.field,
        error = _ref11.error;
    return error ? _extends({}, state, (_extends3 = {}, _extends3[makeConsistentKey(field)] = error, _extends3)) : deleteIn(state, field, false, false);
  }).on(setErrorsInlineState, function (_, errorsInline) {
    return errorsInline;
  }).reset(reset);
  $outerErrorsInline.on(setOrDeleteOuterError, function (state, _ref12) {
    var _extends4;

    var field = _ref12.field,
        error = _ref12.error;
    return error ? _extends({}, state, (_extends4 = {}, _extends4[makeConsistentKey(field)] = error, _extends4)) : deleteIn(state, field, false, false);
  }).on(setOuterErrorsInlineState, function (_, errorsInline) {
    return errorsInline;
  }).on(resetOuterError, function (errors, field) {
    return deleteIn(errors, field, false, false);
  }).reset(reset);
  $fieldsInline.on(setOrDeleteOuterError, function (state, _ref13) {
    var _extends5;

    var field = _ref13.field;
    return _extends({}, state, (_extends5 = {}, _extends5[makeConsistentKey(field)] = _extends({}, state[makeConsistentKey(field)], {
      touchedAfterOuterError: false,
      changedAfterOuterError: false,
      blurredAfterOuterError: false
    }), _extends5));
  }).on(resetOuterFieldStateFlags, function (state) {
    var newState = {};
    Object.entries(state).forEach(function (_ref14) {
      var field = _ref14[0],
          state = _ref14[1];
      return newState[field] = _extends({}, state, {
        touchedAfterOuterError: false,
        changedAfterOuterError: false,
        blurredAfterOuterError: false
      });
    });
    return newState;
  }).on(setFieldState, function (state, _ref15) {
    var _extends6;

    var field = _ref15.field,
        fieldState = _ref15.state;
    return _extends({}, state, (_extends6 = {}, _extends6[makeConsistentKey(field)] = fieldState, _extends6));
  }).on(fieldInit, function (state, _ref16) {
    var _extends7, _extends8;

    var name = _ref16.name,
        validate = _ref16.validate,
        flat = _ref16.flat;
    return state[flat ? name : makeConsistentKey(name)] ? _extends({}, state, (_extends7 = {}, _extends7[flat ? name : makeConsistentKey(name)] = _extends({}, state[flat ? name : makeConsistentKey(name)], initialFieldState, {
      validate: validate
    }), _extends7)) : _extends({}, state, (_extends8 = {}, _extends8[flat ? name : makeConsistentKey(name)] = _extends({}, initialFieldState, {
      validate: validate
    }), _extends8));
  });
  $fieldsInlineInitData.on(fieldInit, function (state, _ref17) {
    var _extends9, _extends10;

    var name = _ref17.name,
        validate = _ref17.validate,
        flat = _ref17.flat;
    return state[flat ? name : makeConsistentKey(name)] ? _extends({}, state, (_extends9 = {}, _extends9[flat ? name : makeConsistentKey(name)] = _extends({}, state[flat ? name : makeConsistentKey(name)], initialFieldState, {
      validate: validate
    }), _extends9)) : _extends({}, state, (_extends10 = {}, _extends10[flat ? name : makeConsistentKey(name)] = _extends({}, initialFieldState, {
      validate: validate
    }), _extends10));
  });
  sample({
    source: $fieldsInlineInitData,
    clock: reset,
    target: $fieldsInline
  });
  $form.on($outerErrorsInline.updates, function (state, outerErrors) {
    return setIn(state, 'hasOuterError', Boolean(Object.keys(outerErrors).length));
  }).on(submit, function (state) {
    return setIn(state, 'submitted', true);
  }).on(setSubmitted, function (state, value) {
    return setIn(state, 'submitted', value);
  }).on($errorsInline.updates, function (state, errorsInline) {
    return setIn(state, 'hasError', Boolean(Object.keys(errorsInline).length));
  }).reset(reset);
  $meta.on(setMeta, function (state, meta) {
    return meta || state;
  }).reset(reset); /// Field {

  sample({
    source: {
      fieldsInline: $fieldsInline,
      outerErrorsInline: $outerErrorsInline
    },
    clock: onFocusFieldBrowser,
    fn: function fn(_ref18, _ref19) {
      var _extends11;

      var fieldsInline = _ref18.fieldsInline,
          outerErrorsInline = _ref18.outerErrorsInline;
      var name = _ref19.name;
      return _extends({}, fieldsInline, (_extends11 = {}, _extends11[name] = _extends({}, fieldsInline[name], {
        active: true,
        touched: true,
        touchedAfterOuterError: Boolean(outerErrorsInline[name])
      }), _extends11));
    },
    target: $fieldsInline
  });
  sample({
    source: {
      fieldsInline: $fieldsInline,
      outerErrorsInline: $outerErrorsInline
    },
    clock: onChangeFieldBrowser,
    fn: function fn(_ref20, _ref21) {
      var _extends12;

      var fieldsInline = _ref20.fieldsInline,
          outerErrorsInline = _ref20.outerErrorsInline;
      var name = _ref21.name;
      return _extends({}, fieldsInline, (_extends12 = {}, _extends12[name] = _extends({}, fieldsInline[name], {
        changed: true,
        changedAfterOuterError: Boolean(outerErrorsInline[name])
      }), _extends12));
    },
    target: $fieldsInline
  });
  sample({
    source: {
      fieldsInline: $fieldsInline,
      outerErrorsInline: $outerErrorsInline
    },
    clock: onBlurFieldBrowser,
    fn: function fn(_ref22, _ref23) {
      var _extends13;

      var fieldsInline = _ref22.fieldsInline,
          outerErrorsInline = _ref22.outerErrorsInline;
      var name = _ref23.name;
      return _extends({}, fieldsInline, (_extends13 = {}, _extends13[name] = _extends({}, fieldsInline[name], {
        active: false,
        blurred: true,
        blurredAfterOuterError: Boolean(outerErrorsInline[name])
      }), _extends13));
    },
    target: $fieldsInline
  }); /// }

  return {
    setValue: setValue,
    setValues: setValues,
    setOrDeleteError: setOrDeleteError,
    setErrorsInlineState: setErrorsInlineState,
    setFieldState: setFieldState,
    setSubmitted: setSubmitted,
    resetOuterFieldStateFlags: resetOuterFieldStateFlags,
    resetOuterErrors: resetOuterErrors,
    setOrDeleteOuterError: setOrDeleteOuterError,
    setOuterErrorsInlineState: setOuterErrorsInlineState,
    validateForm: validateForm,
    submit: submit,
    reset: reset,
    onSubmit: onSubmit,
    setMeta: setMeta,
    $values: $values,
    $errorsInline: $errorsInline,
    $outerErrorsInline: $outerErrorsInline,
    $fieldsInline: $fieldsInline,
    $form: $form,
    $meta: $meta,
    $allFormState: $allFormState,
    onChangeFieldBrowser: onChangeFieldBrowser,
    onFocusFieldBrowser: onFocusFieldBrowser,
    onBlurFieldBrowser: onBlurFieldBrowser,
    fieldInit: fieldInit,
    getName: getName,
    getNameStr: getNameStr,
    name: name
  };
};

var createNameHelper = function createNameHelper() {
  return {
    getPath: getName,
    getStr: getNameStr
  };
};

export { createFieldArray, createForm, createNameHelper, deleteIn, getIn, getInTs, makeNested, removeFromInlineMap, setIn, useError, useFieldArray, useForm };
